"use client";

/**
 * SourceAwareBalance - Shows USDC balance + portfolio value based on current source
 *
 * - Polymarket: USDC on Polygon + Polymarket portfolio value
 * - Kalshi: USDC on Solana + DFlow positions value
 *
 * Wraps itself in Suspense (useSearchParams requirement).
 */

import { Suspense, useCallback, useEffect, useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { TrendingUp, Plus } from "lucide-react";
import CdnImage from "@/components/CdnImage";
import { useCurrentSource } from "@/hooks/useCurrentSource";
import { usePolymarketBalanceContext } from "@/contexts/PolymarketBalanceContext";
import { useSolanaUSDCBalance } from "@/hooks/useSolanaUSDCBalance";
import { useDFlowPortfolioSummary } from "@/hooks/useDFlowPositionsQuery";
import { usePredictPortfolioSummary } from "@/hooks/usePredictPositionsQuery";
import { useBnbBalance } from "@/hooks/useBnbBalance";
import { useHyperliquidBalance } from "@/hooks/useHyperliquidBalance";
import { useDepositModalStore } from "@/stores/useDepositModalStore";
import { useBscDepositModalStore } from "@/stores/useBscDepositModalStore";

// `useFundWallet` from `@privy-io/react-auth/solana` was imported here
// statically — same pattern that hurt ProfileWidget. SourceAwareBalance
// also mounts on every page (sidebar widget at AppLayoutWithSidebar:631
// and :1464), so the import paid the WalletConnect SDK cost (~1.2 MB)
// on every cold load. Now the funding flow lives in the same lazy
// `SolanaFundOnMount` component used by ProfileWidget — it's only mounted
// when the user actually clicks "+" with `source === kalshi`.
const SolanaFundOnMount = dynamic(() => import("@/components/SolanaFundOnMount"), {
  ssr: false,
});

function BalanceContent() {
  const source = useCurrentSource();
  const { balance: polyBalance, portfolioValue: polyPortfolio, loading: polyLoading } = usePolymarketBalanceContext();
  const { balance: solBalance, loading: solLoading, solanaAddress, refetch: refetchSolana } = useSolanaUSDCBalance();
  const { portfolioValue: dflowPortfolio, loading: dflowLoading } = useDFlowPortfolioSummary();
  const { portfolioValue: predictPortfolio, loading: predictPortfolioLoading } = usePredictPortfolioSummary();
  const { open: openDepositModal } = useDepositModalStore();
  const { open: openBscDepositModal } = useBscDepositModalStore();
  // Mounting `SolanaFundOnMount` (lazy chunk + Privy modal) is gated on
  // this state — set when the user clicks "+" with source = kalshi,
  // cleared when the modal flow resolves.
  const [pendingSolanaFundAddress, setPendingSolanaFundAddress] = useState<string | null>(null);

  const { usdtBalance: bnbUsdtBalance, loading: bnbLoading, walletAddress: bnbWalletAddress } = useBnbBalance();
  const { balance: hlBalance, portfolioValue: hlPortfolio, loading: hlLoading } = useHyperliquidBalance();

  const isKalshi = source === "kalshi";
  const isPredict = source === "predict";
  const isHyperliquid = source === "hyperliquid";

  // Fetch Solana balance when user navigates to Kalshi pages
  useEffect(() => {
    if (isKalshi && solBalance === null) {
      refetchSolana();
    }
  }, [isKalshi, solBalance, refetchSolana]);

  const balance = isHyperliquid
    ? hlBalance
    : isPredict
      ? bnbUsdtBalance
      : isKalshi
        ? solBalance
        : polyBalance;
  const portfolioValue = isHyperliquid
    ? hlPortfolio
    : isPredict
      ? predictPortfolio
      : isKalshi
        ? dflowPortfolio
        : polyPortfolio;
  const loading = isHyperliquid ? hlLoading : isPredict ? bnbLoading : isKalshi ? solLoading : polyLoading;
  const portfolioLoading = isHyperliquid
    ? hlLoading
    : isPredict
      ? predictPortfolioLoading
      : isKalshi
        ? dflowLoading
        : polyLoading;
  const portfolioHref = "/portfolio";
  const tokenIcon = isPredict ? "/usdt.png" : "/usdc.png";

  return (
    <div className="auth-only flex items-center gap-0.5 backdrop-blur-xl bg-white/60 dark:bg-white/[0.06] border border-white/30 dark:border-white/[0.08] rounded-[5px] px-1 py-0.5">
      <Link
        href={portfolioHref}
        prefetch={false}
        className="flex items-center gap-1.5 px-2 py-1 rounded-[5px] hover:bg-white dark:hover:bg-[#18181D] transition-colors"
        aria-label="View USDC balance"
      >
        <CdnImage src={isPredict ? "/usdt.png" : "/usdc.png"} alt="" width={14} height={14} className="rounded-[5px]" aria-hidden="true" />
        <span className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums">
          {loading || balance === null ? "..." : balance === 0 ? "$0" : `$${balance.toFixed(2)}`}
        </span>
      </Link>
      <div className="w-px h-3.5 bg-gray-300/60 dark:bg-white/[0.1]" />
      <Link
        href={portfolioHref}
        prefetch={false}
        className="flex items-center gap-1.5 px-2 py-1 rounded-[5px] hover:bg-white dark:hover:bg-[#18181D] transition-colors"
        aria-label="View portfolio value"
      >
        <TrendingUp className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
        <span className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums">
          {portfolioLoading || portfolioValue === null ? "..." : `$${portfolioValue.toFixed(2)}`}
        </span>
      </Link>
      <div className="w-px h-3.5 bg-gray-300/60 dark:bg-white/[0.1]" />
      <div className="relative group/deposit">
        <button
          onClick={() => {
            if (isHyperliquid) {
              // Reuse the onboarding modal — its Step 2 (Deposit) is the
              // canonical "fund my HL margin" UX. Never opens the polymarket
              // deposit (USDC.e on Polygon) which is wrong for HL.
              try {
                window.dispatchEvent(new CustomEvent('frenflow:hl-enable-open'));
              } catch { /* noop */ }
            } else if (isPredict) {
              openBscDepositModal();
            } else if (isKalshi && solanaAddress) {
              // Triggers the lazy import + Privy fund modal; the lazy
              // component unmounts itself via `onComplete` once done.
              setPendingSolanaFundAddress(solanaAddress);
            } else {
              openDepositModal();
            }
          }}
          className="flex items-center justify-center w-6 h-6 rounded-[5px] bg-gray-900 dark:bg-white hover:bg-gray-700 dark:hover:bg-gray-200 transition-colors"
          aria-label="Fund your wallet"
        >
          <Plus className="w-3 h-3 text-white dark:text-black" />
        </button>
      </div>
      {pendingSolanaFundAddress && (
        <SolanaFundOnMount
          address={pendingSolanaFundAddress}
          onComplete={() => setPendingSolanaFundAddress(null)}
        />
      )}
    </div>
  );
}

// Fallback: show the Polymarket balance (default) while Suspense loads
function BalanceFallback() {
  const { balance, portfolioValue, loading } = usePolymarketBalanceContext();
  const { open: openDepositModal } = useDepositModalStore();

  return (
    <div className="auth-only flex items-center gap-0.5 backdrop-blur-xl bg-white/60 dark:bg-white/[0.06] border border-white/30 dark:border-white/[0.08] rounded-[5px] px-1 py-0.5">
      <Link href="/portfolio" prefetch={false} className="flex items-center gap-1.5 px-2 py-1 rounded-[5px] hover:bg-white dark:hover:bg-[#18181D] transition-colors">
        <CdnImage src="/usdc.png" alt="" width={14} height={14} className="rounded-[5px]" aria-hidden="true" />
        <span className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums">
          {loading || balance === null ? "..." : balance === 0 ? "$0" : `$${balance.toFixed(2)}`}
        </span>
      </Link>
      <div className="w-px h-3.5 bg-gray-300/60 dark:bg-white/[0.1]" />
      <Link href="/portfolio" prefetch={false} className="flex items-center gap-1.5 px-2 py-1 rounded-[5px] hover:bg-white dark:hover:bg-[#18181D] transition-colors">
        <TrendingUp className="h-3.5 w-3.5 text-gray-400" />
        <span className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums">
          {loading || portfolioValue === null ? "..." : `$${portfolioValue.toFixed(2)}`}
        </span>
      </Link>
      <div className="w-px h-3.5 bg-gray-300/60 dark:bg-white/[0.1]" />
      <div className="relative group/deposit">
        <button
          onClick={() => openDepositModal()}
          className="flex items-center justify-center w-6 h-6 rounded-[5px] bg-gray-900 dark:bg-white hover:bg-gray-700 dark:hover:bg-gray-200 transition-colors"
          aria-label="Fund your wallet"
        >
          <Plus className="w-3 h-3 text-white dark:text-black" />
        </button>
      </div>
    </div>
  );
}

export default function SourceAwareBalance() {
  return (
    <Suspense fallback={<BalanceFallback />}>
      <BalanceContent />
    </Suspense>
  );
}
